import {Dealer, Dealers} from "../types/Dealers";
import {Map} from "mapbox-gl";
import ActiveMarkerColor from "../constants/ActiveMarkerColor";
import DefaultMarkerColor from "../constants/DefaultMarkerColor";

/**
 * Creates a dealer card
 * @param dealer
 * @param map
 */
export const createDealerCard = (dealer: Dealer, map: Map) => {
	const card = document.createElement('div');
	card.className = 'dealer-card gradient-box';

	// Adding id to make it easier to find the card on a marker click
	card.id = `dealer-card-${dealer.dealerId}`;

	const gradientBoxContentWrapper = document.createElement('div');
	gradientBoxContentWrapper.className = 'gradient-box__content-wrapper h-full';

	const gradientBoxContent = document.createElement('div');
	gradientBoxContent.className =
		'gradient-box__content gradient-box__content--dealer gap-4 h-full';

	const name = document.createElement('h3');
	name.className = 'text-lg lg:text-[21px] leading-none';
	name.innerHTML = dealer.name;
	gradientBoxContent.appendChild(name);

	const addressSection = document.createElement('div');
	addressSection.className = 'dealer-card-section';
	addressSection.innerHTML = `
	<span class="dealer-card-section__icon">
		<svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
			<path d="M7.71429 0C9.76024 0 11.7224 0.812753 13.1691 2.25946C14.6158 3.70617 15.4286 5.66833 15.4286 7.71429C15.4286 10.908 12.9943 14.2543 8.22857 17.8286C8.0802 17.9398 7.89975 18 7.71429 18C7.52883 18 7.34837 17.9398 7.2 17.8286C2.43429 14.2543 0 10.908 0 7.71429C0 5.66833 0.812753 3.70617 2.25946 2.25946C3.70617 0.812753 5.66833 0 7.71429 0ZM7.71429 5.14286C7.0323 5.14286 6.37825 5.41377 5.89601 5.89601C5.41377 6.37825 5.14286 7.0323 5.14286 7.71429C5.14286 8.39627 5.41377 9.05032 5.89601 9.53256C6.37825 10.0148 7.0323 10.2857 7.71429 10.2857C8.39627 10.2857 9.05032 10.0148 9.53256 9.53256C10.0148 9.05032 10.2857 8.39627 10.2857 7.71429C10.2857 7.0323 10.0148 6.37825 9.53256 5.89601C9.05032 5.41377 8.39627 5.14286 7.71429 5.14286Z" fill="#00B3EC"/>
		</svg>
	</span>`;

	if (dealer.dealerLocation) {
		const address = document.createElement('span');
		address.innerHTML = `${dealer.dealerLocation.address}`;
		address.className = 'dealer-card-section__text';
		addressSection.appendChild(address);
		gradientBoxContent.appendChild(addressSection);
	}

	if (dealer.phoneNumber) {
		const phoneSection = document.createElement('span');
		phoneSection.className = 'dealer-card-section flex items-start';
		phoneSection.innerHTML = `
		<span class="dealer-card-section__icon">
			<svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
				<g clip-path="url(#clip0_4002_16155)">
					<path d="M16.2364 12.7747L13.6912 12.4841C13.392 12.449 13.0886 12.4821 12.804 12.581C12.5194 12.68 12.2609 12.8421 12.0479 13.0553L10.2042 14.899C7.35968 13.4523 5.04761 11.1402 3.60092 8.29569L5.45465 6.44196C5.88552 6.01109 6.09594 5.40988 6.0258 4.79865L5.73522 2.27357C5.67841 1.78476 5.44382 1.33392 5.07613 1.00687C4.70843 0.679822 4.2333 0.499417 3.7412 0.500001H2.00771C0.875436 0.500001 -0.0664603 1.4419 0.00368089 2.57418C0.53475 11.1314 7.37853 17.9652 15.9257 18.4962C17.058 18.5664 17.9999 17.6245 17.9999 16.4922V14.7587C18.0099 13.7467 17.2484 12.895 16.2364 12.7747Z" fill="#00B3EC"/>
				</g>
				<defs>
					<clipPath id="clip0_4002_16155">
						<rect width="18" height="18" fill="white" transform="translate(0 0.5)"/>
					</clipPath>
				</defs>
			</svg>
		</span>
		`;

		const phone = document.createElement('span');
		phone.innerHTML = dealer.phoneNumber;
		phone.className = 'dealer-card-section__text';
		phoneSection.appendChild(phone);

		const phoneLink = document.createElement('a');
		phoneLink.href = `tel:${dealer.phoneNumber}`;
		phoneLink.appendChild(phoneSection);

		gradientBoxContent.appendChild(phoneLink);
	}

	if (dealer.email) {
		const emailSection = document.createElement('span');
		emailSection.className = 'dealer-card-section flex items-start';
		emailSection.innerHTML = `
		<span class="dealer-card-section__icon">
			<svg class="text-primary" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
				<path d="m3.62 6.389 8.396 6.724 8.638-6.572-7.69-4.29a1.975 1.975 0 0 0-1.928 0L3.62 6.39Z"/>
				<path d="m22 8.053-8.784 6.683a1.978 1.978 0 0 1-2.44-.031L2.02 7.693a1.091 1.091 0 0 0-.019.199v11.065C2 20.637 3.343 22 5 22h14c1.657 0 3-1.362 3-3.043V8.053Z"/>
			</svg>
		</span>
		`;
		const email = document.createElement('span');
		email.innerHTML = dealer.email;
		email.className = 'dealer-card-section__text';
		emailSection.appendChild(email);


		const emailLink = document.createElement('a');
		emailLink.href = `mailto:${dealer.email}`;
		emailLink.appendChild(emailSection);
		gradientBoxContent.appendChild(emailLink);
	}

	gradientBoxContentWrapper.appendChild(gradientBoxContent);
	card.appendChild(gradientBoxContentWrapper);

	// Add click event to the card
	card.addEventListener('click', () => {
		if (!dealer.dealerLocation) {
			return;
		}

		// Related Marker Element
		const relatedMarkerEl = document.getElementById(`dealer-marker-${dealer.dealerId}`);
		if (!relatedMarkerEl) {
			return;
		}

		const relatedMarkerPath = relatedMarkerEl.querySelector<SVGPathElement>('svg path');
		if (!relatedMarkerPath) {
			return;
		}

		// Make all other cards inactive
		const allCards = document.querySelectorAll('.dealer-card');
		allCards.forEach((card) => {
			card.classList.remove('dealer-card_active');
		});

		// Make all other markers inactive
		const allMarkers = document.querySelectorAll('.mapboxgl-marker');
		allMarkers.forEach((marker) => {
			const markerPath = marker.querySelector<SVGPathElement>('svg path');
			if (!markerPath) {
				return;
			}
			markerPath.style.fill = DefaultMarkerColor;
		});

		// Make the clicked card active/highlighted
		card.classList.add('dealer-card_active');

		// Change the marker color to active
		relatedMarkerPath.style.fill = ActiveMarkerColor;


		// Fly to the dealer location/marker on the map
		map.flyTo({
			center: [dealer.dealerLocation.lng, dealer.dealerLocation.lat],
			zoom: 12,
		});


		// Wait for the flyTo animation to finish and then trigger the marker click
		setTimeout(() => {
			relatedMarkerEl.click();
		}, 1000);
	});

	return card;
}

/**
 * Renders the dealers
 * @param dealers
 * @param mapInstance
 */
export const renderDealersList = (dealers: Dealers, mapInstance: Map) => {

	const dealersContainer = document.getElementById("dealers-list");

	if (!dealersContainer) {
		return;
	}

	dealersContainer.innerHTML = '';

	// Checking if there are any dealers
	if (dealers.length > 0) {
		dealers.forEach((dealer) => {
			const dealerCard = createDealerCard(dealer, mapInstance);
			dealersContainer.appendChild(dealerCard);
		});
	} else {
		const noDealers = document.createElement('span');
		noDealers.className = 'text-center';
		noDealers.innerHTML = 'No search results. Please try again.';
		dealersContainer.appendChild(noDealers);

	}

}

/**
 * Filters the dealers
 * @param searchQuery
 * @param dealers
 * @param mapInstance
 */
export const filterDealers = (searchQuery: string, dealers: Dealers, mapInstance: Map) => {
	const search = searchQuery.toLowerCase();

	const filteredDealers = dealers.filter((dealer) => {
		const dealerName = dealer.name.toLowerCase();

		if (dealer.dealerLocation) {
			const dealerAddress = dealer.dealerLocation.address.toLowerCase()
			return dealerName.includes(search) || dealerAddress.includes(search);
		} else {
			return dealerName.includes(search);
		}

	});

	renderDealersList(filteredDealers, mapInstance);

}
